import React, { useMemo, useRef, useState, useEffect } from 'react';
import { sendUniversalEmail } from '../utils/mailer';
import { supabase } from "../supabaseClient";
import { routes } from "../routes";
import bcrypt from "bcryptjs";
import { useBranding } from '../context/BrandingContext';

function friendlySupabaseError(err) {
  const msg = (err?.message || "").toLowerCase();
  const code = err?.code;
  if (code === '23505' || msg.includes('duplicate') || msg.includes('unique constraint') || msg.includes('clientes_correo_key')) {
    return 'Ese correo ya está registrado. Inicia sesión o usa recuperar contraseña.';
  }
  return 'Intenta nuevamente en unos segundos.';
}

const API_BASE = "https://ubicaciones.paginasweb.cr";
const HACIENDA_AE = "https://api.hacienda.go.cr/fe/ae?identificacion=";

/* ---------- Toast (fallback inline) ---------- */
function Toast({ toast, onClose }) {
  if (!toast) return null;
  const { branding } = useBranding();
  const brandColor = branding.colors.primary;
  const theme = {
    success: { barClass: "bg-green-500", barColor: "#22c55e", ringClass: "ring-green-200" },
    error:   { barClass: "bg-red-500",   barColor: "#ef4444", ringClass: "ring-red-200"   },
    warning: { barClass: "bg-amber-500", barColor: "#f59e0b", ringClass: "ring-amber-200" },
    info:    { barClass: "bg-brand", barColor: brandColor, ringClass: "ring-brand/30" },
  }[toast.variant || "info"];

  return (
    <div className="fixed top-4 right-4 z-[9999] w-[92vw] max-w-sm pointer-events-none">
      <div role="status" aria-live="polite"
        className={`pointer-events-auto shadow-xl rounded-xl border border-gray-100 bg-white overflow-hidden ring-1 ${theme.ringClass} animate-[fadeIn_.18s_ease-out]`}>
        <div className="flex">
          <div className={`${theme.barClass} w-1`} />
          <div className="flex-1 p-3">
            <div className="flex items-start">
              <div className="mr-2 mt-0.5 text-gray-700"><strong>ℹ</strong></div>
              <div className="flex-1">
                {toast.title && <p className="text-sm font-semibold text-gray-900 m-0">{toast.title}</p>}
                {toast.description && <p className="text-sm text-gray-600 mt-1">{toast.description}</p>}
              </div>
              <button onClick={onClose} aria-label="Cerrar" className="ml-2 text-gray-400 hover:text-gray-600">×</button>
            </div>
            {toast.duration > 0 && (
              <div className="mt-3 h-1 w-full bg-gray-100 overflow-hidden rounded">
                <div className={`${theme.barClass} h-full`} style={{ animation: `bar ${toast.duration}ms linear forwards` }} />
              </div>
            )}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes bar { from { width:100% } to { width:0% } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px);} to { opacity:1; transform:translateY(0);} }
      `}</style>
    </div>
  );
}
/* -------------------------------------------- */

export default function RegistroPage({ onNavigate, onLoggedIn = () => {}, onNotify }) {
  const { branding } = useBranding();
  const [formData, setFormData] = useState({
    nombre: "",
    correo: "",
    celular: "",
    contrasena: "",

    requiereFE: false,
    tipoCedula: "01",
    numeroCedula: "",
    razonSocial: "",
    correoFacturacion: "",
    telefonoFacturacion: "",
    actComercial: "",

    provinciaCode: "",
    provinciaName: "",
    cantonCode: "",
    cantonName: "",
    distritoCode: "",
    distritoName: "",
    otrasSenas: "",
  });

  const [loading, setLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);
  const showToast = ({ variant = "info", title, description, duration = 4000 }) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ variant, title, description, duration });
    if (duration > 0) timerRef.current = setTimeout(() => setToast(null), duration);
  };

  // Helpers de validación
  const isEmail = (s) => /\S+@\S+\.\S+/.test((s || "").trim());
  const phoneDigits = (s) => (s || "").replace(/\D/g, "");
  const isPhone = (s) => phoneDigits(s).length >= 8; // CR: mínimo 8 dígitos
  const isStrongPass = (s) => (s || "").length >= 6;

  const baseValid = useMemo(() => {
    return (
      (formData.nombre || "").trim().length > 0 &&
      isEmail(formData.correo) &&
      isPhone(formData.celular) &&
      isStrongPass(formData.contrasena)
    );
  }, [formData.nombre, formData.correo, formData.celular, formData.contrasena]);

  // Catálogos CR
  const [provincias, setProvincias] = useState([]);
  const [cantones, setCantones] = useState([]);
  const [distritos, setDistritos] = useState([]);

  // Actividades de Hacienda
  const [actividades, setActividades] = useState([]);
  const [actividadesLoading, setActividadesLoading] = useState(false);
  const [actividadesError, setActividadesError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/provincias.json`);
        const json = await resp.json();
        const list = Object.entries(json).map(([code, name]) => ({ code, name }));
        setProvincias(list);
      } catch (e) {
        console.error("Error cargando provincias:", e);
      }
    })();
  }, []);

  useEffect(() => {
    if (!formData.provinciaCode) { setCantones([]); setDistritos([]); return; }
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/provincia/${formData.provinciaCode}/cantones.json`);
        const json = await resp.json();
        const list = Object.entries(json).map(([code, name]) => ({ code, name }));
        setCantones(list);
        setFormData((prev) => ({ ...prev, cantonCode: "", cantonName: "", distritoCode: "", distritoName: "" }));
        setDistritos([]);
      } catch (e) {
        console.error("Error cargando cantones:", e);
      }
    })();
  }, [formData.provinciaCode]);

  useEffect(() => {
    if (!formData.provinciaCode || !formData.cantonCode) { setDistritos([]); return; }
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/provincia/${formData.provinciaCode}/canton/${formData.cantonCode}/distritos.json`);
        const json = await resp.json();
        const list = Object.entries(json).map(([code, name]) => ({ code, name }));
        setDistritos(list);
        setFormData((prev) => ({ ...prev, distritoCode: "", distritoName: "" }));
      } catch (e) {
        console.error("Error cargando distritos:", e);
      }
    })();
  }, [formData.provinciaCode, formData.cantonCode]);

  // === Consulta automática a Hacienda cuando hay CÉDULA y requiere FE ===
  useEffect(() => {
    if (!formData.requiereFE) {
      setActividades([]); setActividadesError(""); setFormData((p) => ({ ...p, actComercial: "" }));
      return;
    }
    const id = (formData.numeroCedula || "").trim();
    if (id.length < 9) {
      setActividades([]); setActividadesError(""); setFormData((p) => ({ ...p, actComercial: "" }));
      return;
    }

    const controller = new AbortController();
    setActividadesLoading(true);
    setActividadesError("");

    const t = setTimeout(async () => {
      try {
        const res = await fetch(`${HACIENDA_AE}${encodeURIComponent(id)}`, { signal: controller.signal });
        if (!res.ok) throw new Error(`Hacienda respondió ${res.status}`);
        const json = await res.json();
        const acts = Array.isArray(json.actividades) ? json.actividades : [];
        const activas = acts.filter(a => a.estado === "A");
        const usable = (activas.length ? activas : acts).map(a => ({
          codigo: String(a.codigo),
          descripcion: a.descripcion || String(a.codigo)
        }));
        setActividades(usable);
        if (usable.length === 1) setFormData(p => ({ ...p, actComercial: usable[0].codigo }));
        else if (!usable.find(a => a.codigo === formData.actComercial)) setFormData(p => ({ ...p, actComercial: "" }));
      } catch (e) {
        console.error("Hacienda AE error:", e);
        setActividades([]); setActividadesError("No pudimos consultar Hacienda. Revisá la cédula o intenta más tarde.");
        setFormData(p => ({ ...p, actComercial: "" }));
      } finally {
        setActividadesLoading(false);
      }
    }, 500);

    return () => { clearTimeout(t); controller.abort(); };
  }, [formData.requiereFE, formData.numeroCedula]);

  // Validación FE completa cuando el toggle está activo
  const feValid = useMemo(() => {
    if (!formData.requiereFE) return true;
    const correoOk = isEmail(formData.correoFacturacion);
    const telOk = isPhone(formData.telefonoFacturacion);
    const baseOk =
      formData.tipoCedula &&
      (formData.numeroCedula || "").trim().length > 0 &&
      (formData.razonSocial || "").trim().length > 0 &&
      correoOk &&
      telOk;
    const actOk = actividades.length > 0 ? !!formData.actComercial : true;
    return baseOk && actOk;
  }, [
    formData.requiereFE,
    formData.tipoCedula,
    formData.numeroCedula,
    formData.razonSocial,
    formData.correoFacturacion,
    formData.telefonoFacturacion,
    formData.actComercial,
    actividades.length
  ]);

  const isFormValid = baseValid && feValid;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "provinciaCode") {
      const sel = provincias.find((p) => p.code === value);
      setFormData((prev) => ({ ...prev, provinciaCode: value, provinciaName: sel?.name || "" }));
      return;
    }
    if (name === "cantonCode") {
      const sel = cantones.find((c) => c.code === value);
      setFormData((prev) => ({ ...prev, cantonCode: value, cantonName: sel?.name || "" }));
      return;
    }
    if (name === "distritoCode") {
      const sel = distritos.find((d) => d.code === value);
      setFormData((prev) => ({ ...prev, distritoCode: value, distritoName: sel?.name || "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!baseValid) {
      showToast({ variant: "warning", title: "Campos incompletos", description: "Completá nombre, correo, teléfono y una contraseña de al menos 6 caracteres." });
      return;
    }
    if (formData.requiereFE && !feValid) {
      showToast({ variant: "warning", title: "Faltan datos de FE", description: "Completá todos los campos de Factura Electrónica." });
      return;
    }

    setLoading(true);
    try {
      const hashedPassword = await bcrypt.hash(formData.contrasena, 10);

      const { data: exists } = await supabase
        .from('clientes')
        .select('id')
        .eq('correo', formData.correo.trim())
        .maybeSingle();

      if (exists) {
        showToast({
          variant: 'warning',
          title: 'Correo ya registrado',
          description: 'Inicia sesión o usa recuperar contraseña.',
        });
        setLoading(false);
        return;
      }

      const { data: inserted, error } = await supabase
        .from("clientes")
        .insert([{
          nombre: formData.nombre,
          correo: formData.correo.trim(),
          celular: phoneDigits(formData.celular),
          contrasena_hash: hashedPassword,

          requiere_fe: formData.requiereFE,
          tipo_cedula: formData.requiereFE ? formData.tipoCedula : null,
          numero_cedula: formData.requiereFE ? (formData.numeroCedula || "").trim() : null,
          razon_social: formData.requiereFE ? (formData.razonSocial || "").trim() : null,
          correo_facturacion: formData.requiereFE ? (formData.correoFacturacion || "").trim() : null,
          telefono_facturacion: formData.requiereFE ? phoneDigits(formData.telefonoFacturacion) : null,
          act_comercial: formData.requiereFE ? (formData.actComercial || null) : null,

          provincia: formData.provinciaName || null,
          canton: formData.cantonName || null,
          distrito: formData.distritoName || null,
          otras_senas: formData.otrasSenas || null,
        }])
        .select('id, nombre, correo')
        .single();

      if (error) {
        console.error("Supabase insert error:", error);
        showToast({ variant: "error", title: "No se pudo registrar", description: friendlySupabaseError(error) });
        setLoading(false);
        return;
      }

      // 1) AUTO-LOGIN
      const user = { id: inserted.id, nombre: inserted.nombre, correo: inserted.correo };
      onLoggedIn?.(user);

      // 2) Email de bienvenida
      try {
        const toEmail = (inserted?.correo || formData?.correo || '').trim();
        if (toEmail) {
          await sendUniversalEmail({
            to_email: toEmail,
            reply_to: "info@elancla.store",
            customer_name: inserted?.nombre?.split(/\s+/)[0] || "Cliente",
            title: "¡Bienvenido a El Ancla CR!",
            preheader: "Gracias por registrarte. Hay ofertas esperándote 👀",
            intro_html: "Tu cuenta fue creada y ya podés iniciar sesión.",
            body_html: '<p style="margin:0 0 16px 0;color:#374151;">Explorá perfumes y tecnología seleccionados para vos.</p>',
            cta_label: "Ver catálogo",
            cta_url: `${window.location.origin}/`,
          }, branding);
        }
      } catch (mailErr) {
        console.error('Bienvenida error:', mailErr);
      }

      // 3) Toast global + navegar
      onNotify?.({
        variant: "success",
        title: "¡Bienvenido!",
        description: "Tu cuenta fue creada y ya iniciaste sesión.",
        duration: 4500,
      });
      onNavigate?.(routes.home);
    } catch (err) {
      console.error(err);
      showToast({ variant: "error", title: "Error inesperado", description: err?.message || "Ocurrió un problema al registrarte." });
    } finally {
      setLoading(false);
    }
  };

  const tiposCedula = useMemo(
    () => [
      { value: "01", label: "Física" },
      { value: "02", label: "Jurídica" },
      { value: "03", label: "DIMEX" },
      { value: "04", label: "NITE" },
    ],
    []
  );

  /* ===================== RENDER ===================== */
  return (
    <div className="min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8 py-10">
      <div className="max-w-7xl mx-auto">
        <header className="mb-8">
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">
            Crea tu cuenta <span className="text-brand">El Ancla CR</span>
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Los campos marcados con <span className="text-red-500 font-semibold">*</span> son obligatorios.
          </p>
        </header>

        <form onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Columna izquierda: Cuenta + Dirección */}
            <div className="space-y-6">
              {/* Card: Datos de cuenta */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">Datos de la cuenta</h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Nombre completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="nombre"
                      placeholder="Ej: Ever Leitón"
                      value={formData.nombre}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      className="w-full border rounded-lg p-3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Correo electrónico <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="correo"
                      placeholder="tucorreo@dominio.com"
                      value={formData.correo}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      className="w-full border rounded-lg p-3"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Teléfono <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="celular"
                      placeholder="+506 8888 8888"
                      value={formData.celular}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      className="w-full border rounded-lg p-3"
                    />
                    <p className="text-xs text-gray-400 mt-1">Mínimo 8 dígitos (se aceptan espacios o guiones).</p>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-700 mb-1">
                      Contraseña <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="contrasena"
                      placeholder="Mínimo 6 caracteres"
                      value={formData.contrasena}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      minLength={6}
                      className="w-full border rounded-lg p-3"
                    />
                  </div>
                </div>
              </section>

              {/* Card: Dirección */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Dirección (opcional)</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Provincia</label>
                    <select
                      name="provinciaCode"
                      value={formData.provinciaCode}
                      onChange={handleChange}
                      className="w-full border rounded-lg p-3 bg-white"
                    >
                      <option value="">Seleccione provincia</option>
                      {provincias.map((p) => <option key={p.code} value={p.code}>{p.name}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Cantón</label>
                    <select
                      name="cantonCode"
                      value={formData.cantonCode}
                      onChange={handleChange}
                      className="w-full border rounded-lg p-3 bg-white"
                      disabled={!formData.provinciaCode}
                    >
                      <option value="">{formData.provinciaCode ? "Seleccione cantón" : "Seleccione provincia primero"}</option>
                      {cantones.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Distrito</label>
                    <select
                      name="distritoCode"
                      value={formData.distritoCode}
                      onChange={handleChange}
                      className="w-full border rounded-lg p-3 bg-white"
                      disabled={!formData.cantonCode}
                    >
                      <option value="">{formData.cantonCode ? "Seleccione distrito" : "Seleccione cantón primero"}</option>
                      {distritos.map((d) => <option key={d.code} value={d.code}>{d.name}</option>)}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-sm text-gray-600 mb-1">Otras señas</label>
                    <input
                      type="text"
                      name="otrasSenas"
                      placeholder="Casa #, referencia, etc."
                      value={formData.otrasSenas}
                      onChange={handleChange}
                      className="w-full border rounded-lg p-3"
                    />
                  </div>
                </div>
              </section>
            </div>

            {/* Columna derecha: Factura Electrónica (sticky) */}
            <aside className="space-y-6 lg:sticky lg:top-6 self-start">
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-900">Factura Electrónica</h2>
                  <div className="flex items-center gap-2">
                    <label htmlFor="requiereFE" className="text-sm text-gray-700">Necesito FE</label>
                    <input
                      type="checkbox"
                      id="requiereFE"
                      name="requiereFE"
                      checked={formData.requiereFE}
                      onChange={handleChange}
                      className="h-4 w-4 accent-brand"
                    />
                  </div>
                </div>

                {!formData.requiereFE ? (
                  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                    Si marcás <span className="font-medium">Necesito FE</span>, te pediremos
                    los datos para generar las facturas a tu nombre.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Tipo de cédula <span className="text-red-500">*</span>
                        </label>
                        <select
                          name="tipoCedula"
                          value={formData.tipoCedula}
                          onChange={handleChange}
                          className="w-full border rounded-lg p-3 bg-white"
                          required
                          aria-required="true"
                        >
                          {[
                            { value: "01", label: "Física" },
                            { value: "02", label: "Jurídica" },
                            { value: "03", label: "DIMEX" },
                            { value: "04", label: "NITE" },
                          ].map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Número de cédula <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          name="numeroCedula"
                          placeholder="Ej: 207680749"
                          value={formData.numeroCedula}
                          onChange={handleChange}
                          required
                          aria-required="true"
                          className="w-full border rounded-lg p-3"
                        />
                      </div>
                    </div>

                    {/* Actividad Económica (Hacienda) */}
                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Actividad económica (Hacienda){actividades.length > 0 && <span className="text-red-500"> *</span>}
                      </label>
                      <select
                        name="actComercial"
                        value={formData.actComercial}
                        onChange={handleChange}
                        className="w-full border rounded-lg p-3 bg-white"
                        required={actividades.length > 0}
                        aria-required={actividades.length > 0 ? "true" : "false"}
                        disabled={actividadesLoading || !!actividadesError || actividades.length === 0}
                      >
                        <option value="">
                          {actividadesLoading
                            ? "Consultando Hacienda…"
                            : actividadesError
                              ? "No disponible"
                              : actividades.length === 0
                                ? "Sin actividades disponibles"
                                : "Seleccione actividad"}
                        </option>
                        {actividades.map((a) => (
                          <option key={a.codigo} value={a.codigo}>
                            {a.descripcion}
                          </option>
                        ))}
                      </select>
                      {actividadesError && <p className="text-xs text-red-600 mt-1">{actividadesError}</p>}
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Razón Social <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="razonSocial"
                        value={formData.razonSocial}
                        onChange={handleChange}
                        required
                        aria-required="true"
                        className="w-full border rounded-lg p-3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Correo de facturación <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="correoFacturacion"
                        placeholder="facturacion@correo.com"
                        value={formData.correoFacturacion}
                        onChange={handleChange}
                        required
                        aria-required="true"
                        className="w-full border rounded-lg p-3"
                      />
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 mb-1">
                        Teléfono de facturación <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="telefonoFacturacion"
                        value={formData.telefonoFacturacion}
                        onChange={handleChange}
                        required
                        aria-required="true"
                        className="w-full border rounded-lg p-3"
                      />
                    </div>
                  </div>
                )}
              </section>
            </aside>
          </div>

          {/* CTA principal */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={loading || !isFormValid}
              className={`w-full text-white py-3 rounded-lg font-semibold transition-colors
                ${loading || !isFormValid ? "bg-brand/60 cursor-not-allowed" : "bg-brand hover:bg-brand/90"}`}
            >
              {loading ? "Registrando..." : "Registrarme"}
            </button>
          </div>
        </form>
      </div>

      {/* Toast */}
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
